import { Link } from 'react-router-dom';
import { FindingRow } from '@/components/ds/FindingRow';
import {
  RELATIONS,
  FINDING,
  SOURCE,
  HEADING,
  LEAD,
  ATTRIBUTION,
  LINK_TEXT,
} from '@/pages/crossPageContent';

/**
 * The cross-page analysis section, shared by / and /ux-auditor/.
 *
 * ── WHY ONE COMPONENT AND NOT TWO SECTIONS ──────────────────────────────────
 *
 * Both pages already carry a `multi-page-section` that says the same three
 * things in the same three cards, and that duplication is maintained by hand.
 * This section quotes a record, so a second hand-maintained copy is a second
 * place for the quotation to drift out of agreement with the fixture while the
 * pin still passes against the first. One component, mounted twice.
 *
 * ── THE QUOTATION IS RENDERED BY FindingRow ─────────────────────────────────
 *
 * Not a restyled blockquote. FindingRow is what /sample-report/ and the report
 * itself use, so a reader who clicks through meets the same row they just read,
 * and the severity chip carries the three-step meter rather than colour alone
 * (ruled 2026-08-17). A second chip implementation here would be a second
 * chance to drop the meter.
 *
 * ── ADDITIVE ────────────────────────────────────────────────────────────────
 *
 * This adds a section. It changes no heading, no heading level, no existing
 * copy and no existing link on either host page, per the content-preservation
 * rule in CLAUDE.md.
 */
export function CrossPageAnalysis() {
  return (
    <section
      className="uxa-landing-section uxa-landing-section--center uxa-pt-xl"
      data-testid="cross-page-section"
    >
      <span className="section-label">Cross-Page</span>
      <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>
        {HEADING}
      </h2>
      <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>
        {LEAD}
      </p>

      {/* The quotation. Left-aligned inside a centred section because it is a
          row from a report, and a centred finding row reads as a slogan. */}
      <div style={{ maxWidth: 720, margin: '48px auto 0', textAlign: 'left' }}>
        <FindingRow
          severity={FINDING.severity}
          title={FINDING.title}
          dimension={FINDING.dimensionLabel}
          testId="cross-page-finding"
        />
        <p
          className="uxa-lead"
          style={{ color: 'var(--ink-2)', margin: '20px 0 0', textAlign: 'left' }}
        >
          {ATTRIBUTION}{' '}
          <Link to={SOURCE.reportPath} data-testid="cross-page-report-link">
            {LINK_TEXT}
          </Link>
        </p>
      </div>

      {/* The closed set, so the quoted `outlier` reads as one of four things
          the pass can report rather than the only one. */}
      <div className="uxa-grid-3" style={{ marginTop: 56 }}>
        {RELATIONS.map((r) => (
          <div key={r.name} className="uxa-feature-card">
            <div className="uxa-feature-card__bloom" aria-hidden="true" />
            <h3 className="uxa-h3" style={{ margin: '0 0 8px' }}>{r.name}</h3>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{r.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
